import { format } from 'date-fns';

const bankHolidaysData = require('../assets/data/bank-holidays.json');

export interface DateInputParts {
  day: string;
  month: string;
  year: string;
}

function isDateInputParts(value: unknown): value is DateInputParts {
  return (
    typeof value === "object" &&
    value !== null &&
    "day" in value &&
    "month" in value &&
    "year" in value
  );
}

const ISO_DATE_REGEX = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const DMY_DATE_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export function getCurrentDateTimeUKFormatted(): string {
  const date = new Date();

  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false // 24-hour clock
  });

  const parts = formatter.formatToParts(date);

  let day = "";
  let month = "";
  let year = "";
  let hour = "";
  let minute = "";
  let second = "";

  for (const part of parts) {
    if (part.type === "day") day = part.value;
    if (part.type === "month") month = part.value;
    if (part.type === "year") year = part.value;
    if (part.type === "hour") hour = part.value;
    if (part.type === "minute") minute = part.value;
    if (part.type === "second") second = part.value;
  }

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

export function isUkBankHoliday(date: Date): boolean {
  const dateString = format(date, 'yyyy-MM-dd');
  const englandWalesHolidays = bankHolidaysData['england-and-wales']?.events || [];
  return englandWalesHolidays.some((event: any) => event.date === dateString);
}

export function normalizeDateBoundary(value?: string | Date | null): string | null {
  if (!value) return null;

  if (value instanceof Date) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(
      value.getDate()
    ).padStart(2, "0")}`;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const parsedParts = parseDateParts(trimmedValue);
  if (!parsedParts) return null;

  return toIsoDateString(parsedParts);
}

export function parseDateParts(value?: string | Date | null): DateInputParts | null {
  if (!value) return null;

  const normalizedValue = value instanceof Date ? normalizeDateBoundary(value) : value.trim();
  if (!normalizedValue) return null;

  const isoMatch = normalizedValue.match(ISO_DATE_REGEX);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return {
      year,
      month,
      day,
    };
  }

  const dmyMatch = normalizedValue.match(DMY_DATE_REGEX);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    return {
      year,
      month,
      day,
    };
  }

  return null;
}

export function toIsoDateString(parts: DateInputParts): string {
  return `${parts.year}-${parts.month.padStart(2, "0")}-${parts.day.padStart(2, "0")}`;
}

export function formatDateForDisplay(value?: string | Date | null): string {
  const parts = parseDateParts(value);
  if (!parts) {
    return typeof value === "string" ? value : "";
  }

  return `${parts.day.padStart(2, "0")}/${parts.month.padStart(2, "0")}/${parts.year}`;
}

export function hasCompleteDateParts(parts: DateInputParts): boolean {
  return Boolean(parts.day && parts.month && parts.year.length === 4);
}

export function isValidDateParts(parts: DateInputParts): boolean {
  if (!hasCompleteDateParts(parts)) return false;

  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);

  if ([day, month, year].some((value) => Number.isNaN(value))) return false;

  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function parseDateOnly(value?: string | Date | null): Date | null {
  const normalizedValue = normalizeDateBoundary(value);
  if (!normalizedValue) return null;

  const parts = parseDateParts(normalizedValue);
  if (!parts || !isValidDateParts(parts)) return null;

  return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
}

export function isDateWithinRange(
  value: Date | string | DateInputParts,
  min?: string | Date | null,
  max?: string | Date | null
): boolean {
  const date =
    value instanceof Date
      ? new Date(value.getFullYear(), value.getMonth(), value.getDate())
      : isDateInputParts(value)
        ? parseDateOnly(toIsoDateString(value))
        : parseDateOnly(value);

  if (!date) return false;

  const minDate = parseDateOnly(min);
  const maxDate = parseDateOnly(max);

  if (minDate && date < minDate) return false;
  if (maxDate && date > maxDate) return false;

  return true;
}


